import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, Loader2, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { projectId } from '../utils/supabase/info';
import { formatDate } from '../utils/formatDate';

/**
 * ActivationStatusCard — an engagement's activation loop on the client page (RESTORE_PLAN R2.6,
 * from ActivationPanel): the five steps (SOW uploaded, objectives extracted, client lead
 * assigned, onboarding email sent, onboarding complete) with the overall count, the client
 * lead's onboarding status, and the form that assigns the client lead and sends their
 * onboarding email (`POST /engagements/:id/assign-cel`, the only sender of the CEL onboarding
 * link). The SOW itself is uploaded and analyzed on the SOWs tab.
 */

export interface ActivationStatus {
  engagementId: string;
  engagementName?: string;
  companyName?: string;
  sowUploaded: boolean;
  sowFileName: string | null;
  sowExtractedChars?: number;
  hloCount: number;
  hlosAiExtracted: number;
  hlosManual: number;
  celAssigned: boolean;
  celName: string | null;
  celEmail: string | null;
  onboardingEmailSent: boolean;
  onboardingEmailSentAt: string | null;
  accountCreated: boolean;
  hlosReviewed: boolean;
  teamInvited: boolean;
  teamMembersInvited?: number;
  onboardingComplete: boolean;
}

export interface ActivationStep {
  label: string;
  done: boolean;
  detail?: string;
}

/** The five steps, in order, with what each one found. */
export function activationSteps(s: ActivationStatus): ActivationStep[] {
  return [
    { label: 'SOW uploaded', done: s.sowUploaded, detail: s.sowUploaded ? `${s.sowFileName ?? 'SOW'}${s.sowExtractedChars ? ` (${s.sowExtractedChars.toLocaleString('en-US')} characters read)` : ''}` : undefined },
    { label: 'Objectives extracted', done: s.hloCount > 0, detail: s.hloCount > 0 ? `${s.hloCount} objectives (${s.hlosAiExtracted} by AI, ${s.hlosManual} added)` : undefined },
    { label: 'Client lead assigned', done: s.celAssigned, detail: s.celAssigned ? `${s.celName ?? ''}${s.celEmail ? ` (${s.celEmail})` : ''}` : undefined },
    { label: 'Onboarding email sent', done: s.onboardingEmailSent, detail: s.onboardingEmailSent && s.onboardingEmailSentAt ? `Sent ${formatDate(s.onboardingEmailSentAt, { month: 'short', day: 'numeric' })}` : undefined },
    { label: 'Onboarding complete', done: s.onboardingComplete, detail: s.onboardingComplete ? 'The client lead finished the onboarding wizard' : s.accountCreated ? 'Account created; the wizard is in progress' : undefined },
  ];
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

interface ActivationStatusCardProps {
  companyId: string;
  engagementId?: string | null;
  accessToken: string;
  /** Opens the SOWs tab, where the SOW is uploaded and analyzed. */
  onOpenSows?: () => void;
}

export function ActivationStatusCard({ companyId, engagementId, accessToken, onOpenSows }: ActivationStatusCardProps) {
  const [status, setStatus] = useState<ActivationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [celName, setCelName] = useState('');
  const [celEmail, setCelEmail] = useState('');
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = engagementId ? `?engagementId=${encodeURIComponent(engagementId)}` : '';
      const res = await fetch(`${BASE}/activation-status/${encodeURIComponent(companyId)}${query}`, { headers: { Authorization: `Bearer ${accessToken}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus(null);
        setError(data.error || 'Could not load the activation status');
        return;
      }
      setStatus(data.status ?? null);
    } catch {
      setError('Could not load the activation status');
    } finally {
      setLoading(false);
    }
  }, [companyId, engagementId, accessToken]);

  useEffect(() => { void load(); }, [load]);

  const assign = async () => {
    if (!status?.engagementId || !celName.trim() || !celEmail.trim()) return;
    setAssigning(true);
    try {
      const res = await fetch(`${BASE}/engagements/${encodeURIComponent(status.engagementId)}/assign-cel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ celName: celName.trim(), celEmail: celEmail.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        toast.error(data.error || 'Could not assign the client lead');
        return;
      }
      toast.success(`Onboarding email sent to ${celEmail.trim()}`);
      setCelName('');
      setCelEmail('');
      await load();
    } catch {
      toast.error('Could not assign the client lead');
    } finally {
      setAssigning(false);
    }
  };

  const steps = status ? activationSteps(status) : [];
  const doneCount = steps.filter((s) => s.done).length;
  const current = steps.findIndex((s) => !s.done);

  return (
    <Card data-testid="activation-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle>Activation</CardTitle>
            <CardDescription>From the signed SOW to a client lead who has finished onboarding</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload the activation status">
            <RefreshCw className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading…</p>
        ) : !status ? (
          <p className="text-muted-foreground">{error || 'No activation data for this engagement yet.'}</p>
        ) : (
          <>
            <ol className="space-y-1.5" data-testid="activation-steps">
              {steps.map((step, i) => (
                <li key={step.label} className="flex items-start gap-3" data-done={step.done}>
                  {step.done
                    ? <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-500" aria-hidden="true" />
                    : <Circle className={`mt-0.5 size-5 shrink-0 ${i === current ? 'text-blue-500' : 'text-slate-300'}`} aria-hidden="true" />}
                  <div className="min-w-0">
                    <p className={step.done ? 'text-slate-600' : i === current ? '' : 'text-slate-400'}>{i + 1}. {step.label}</p>
                    {step.detail && <p className="text-muted-foreground">{step.detail}</p>}
                  </div>
                </li>
              ))}
            </ol>
            <div>
              <div className="mb-1 flex items-center justify-between text-muted-foreground">
                <span>Overall</span>
                <span data-testid="activation-overall">{doneCount}/5</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(doneCount / 5) * 100}%` }} />
              </div>
            </div>

            {!status.sowUploaded && onOpenSows && (
              <Button variant="outline" size="sm" onClick={onOpenSows}>Upload and analyze the SOW</Button>
            )}

            {status.celAssigned ? (
              <div className="space-y-1.5 border-t pt-3" data-testid="onboarding-status">
                <p>Onboarding status</p>
                {[
                  { label: 'Account created', ok: status.accountCreated, yes: 'Yes', no: 'Pending' },
                  { label: 'Objectives reviewed', ok: status.hlosReviewed, yes: 'Yes', no: 'Pending' },
                  { label: 'Team invited', ok: status.teamInvited, yes: `${status.teamMembersInvited ?? 0} invited`, no: 'Pending' },
                  { label: 'Complete', ok: status.onboardingComplete, yes: 'Done', no: 'In progress' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{row.label}</span>
                    <Badge variant="outline" className={row.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}>{row.ok ? row.yes : row.no}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <form
                className="space-y-2 border-t pt-3"
                data-testid="assign-client-lead"
                onSubmit={(e) => { e.preventDefault(); void assign(); }}
              >
                <p>Assign the client lead</p>
                <p className="text-muted-foreground">They get the onboarding email and set up the client side: goals, team, timeline.</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="cel-name">Name</Label>
                    <Input id="cel-name" value={celName} onChange={(e) => setCelName(e.target.value)} autoComplete="off" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cel-email">Email</Label>
                    <Input id="cel-email" type="email" value={celEmail} onChange={(e) => setCelEmail(e.target.value)} autoComplete="off" />
                  </div>
                </div>
                <Button type="submit" size="sm" disabled={assigning || !celName.trim() || !celEmail.trim()}>
                  {assigning ? <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" /> : <Send className="mr-2 size-4" aria-hidden="true" />}
                  Send the onboarding email
                </Button>
              </form>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
